// Libs
import ApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useMemo, useState } from "react";
// Types
import { ChartSetOne } from "../Libs/Types/ChartTypes";
// Utils
import {
  CHART_AXIS_FALLBACK,
  chartColors,
  chartHeight,
  humanize,
} from "../Libs/Utils/utils";
// UI-Components
import ChartFallBack from "../UI-Components/ChartFallback";

export default function BarChart({
  chartData,
  dataKey,
  isHorizontal,
}: {
  chartData: ChartSetOne["BarChart"];
  dataKey: string;
  isHorizontal?: boolean;
}) {
  // Assign a unique color to this chart instance ONCE
  const [chartColor] = useState(() => chartColors);

  // If you have multi-series, you can generate a list of random colors ONCE per chart
  const colorList = useMemo(() => {
    if (!chartData?.yaxis) return [];
    return chartData.yaxis.map(() => chartColors);
  }, [chartData?.yaxis]);
  const fYData: object[] = [];
  if (chartData?.ytitle) {
    chartData.ytitle.map((value, key) => {
      let temp = {};
      if (key === 1) {
        temp = {
          ...value,
          ...{
            opposite: true,
            labels: {
              style: { fontSize: "8px" },
              formatter: (val: number) =>
                typeof val === "number" ? val.toFixed(0) : val,
            },
          },
        };
      } else {
        temp = {
          ...value,
          ...{
            labels: {
              style: { fontSize: "8px" },
              formatter: (val: number) =>
                typeof val === "number" ? val.toFixed(0) : val,
            },
          },
        };
      }
      fYData.push(temp);
      return true;
    });
  }
  const option: ApexOptions = {
    chart: {
      fontFamily: "Poppins",
      toolbar: {
        show: true,
      },
    },
    legend: {
      show: false,
    },
    title: {
      style: { fontSize: "14px", fontWeight: "900" },
      text:
        chartData !== undefined
          ? chartData["title"]
          : CHART_AXIS_FALLBACK["title"],
      align: "left",
    },
    dataLabels: {
      style: {
        fontSize: "8px",
      },
    },
    plotOptions: {
      bar: {
        horizontal: isHorizontal,
        columnWidth: "50%",
        // barHeight: "90%",
        distributed: true, // this line is mandatory
        borderRadius: isHorizontal ? 0 : 10, // Adjust the radius as desired
        borderRadiusApplication: "end", // Apply border radius to both ends
        dataLabels: {
          orientation: isHorizontal ? "horizontal" : "vertical",
        },
      },
    },
    fill: {
      colors: colorList.length > 0 ? colorList : [chartColor],
    },
    colors: colorList.length > 0 ? colorList : [chartColor],
    xaxis: {
      categories:
        chartData !== undefined ? chartData["xaxis"]["data"] : undefined,
      labels: {
        style: {
          fontSize: "8px",
        },
        rotate: -90,
      },
      title: {
        text:
          chartData !== undefined
            ? humanize(chartData["xaxis"]["name"])
            : CHART_AXIS_FALLBACK["x"],
      },
    },
    tooltip: {
      y: {
        title: {
          formatter: function (seriesName) {
            return humanize(seriesName);
          },
        },
      },
    },
    yaxis: fYData,
  };

  const series: any =
    chartData !== undefined && Array.isArray(chartData["yaxis"]) === true
      ? chartData["yaxis"]
      : undefined;

  return (
    <div className="my-2 chartWrp rounded-lg w-full p-2">
      {series !== undefined &&
      option.xaxis?.categories !== undefined &&
      chartData["ytitle"] !== undefined ? (
        <ApexChart
          type="bar"
          options={option}
          series={series}
          height={chartHeight}
          width={"100%"}
          key={dataKey}
        />
      ) : (
        <ChartFallBack />
      )}
    </div>
  );
}
